<template>
  <b-sidebar
    id="edit-sales-type-sidebar"
    :visible="!!selectedSalesTypeId"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => !val && $emit('updated')"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Edit Sales Type</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <b-overlay :show="isLoading" spinner-variant="primary">
        <validation-observer ref="refFormObserver">
          <!-- Form -->
          <b-form
            slot-scope="{ validate }"
            class="p-2"
            @submit.prevent="validate().then(onSubmit)"
            @reset.prevent="resetForm"
          >
            <!-- Name -->
            <validation-provider #default="validationContext" name="Name" rules="required">
              <b-form-group label="Name" label-for="sales-type-name">
                <b-form-input
                  id="sales-type-name"
                  v-model="salesTypeData.name"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Enter sales type name"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Code -->
            <validation-provider #default="validationContext" name="Code" rules="required">
              <b-form-group label="Code" label-for="sales-type-code">
                <b-form-input
                  id="sales-type-code"
                  v-model="salesTypeData.code"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Enter sales type code"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Prefix -->
            <validation-provider #default="validationContext" name="Prefix" rules="required">
              <b-form-group label="Prefix" label-for="sales-type-prefix">
                <b-form-input
                  id="sales-type-prefix"
                  v-model="salesTypeData.prefix"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Enter sales type prefix"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Is Active -->
            <b-form-group>
              <b-form-checkbox
                id="sales-type-active"
                v-model="salesTypeData.isActive"
                name="sales-type-active"
                value="true"
                unchecked-value="false"
              >
                Active
              </b-form-checkbox>
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                :disabled="isSubmitting"
              >
                <b-spinner v-if="isSubmitting" small class="mr-1" />
                Update
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from '@vue/composition-api';
import { required } from '@validations';
import formValidation from '@core/comp-functions/forms/form-validation';
import Ripple from 'vue-ripple-directive';
import store from '@/store';

export default {
  name: 'SalesTypeListEdit',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BOverlay,
    BSpinner,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    selectedSalesTypeId: {
      type: [Number],
      default: null,
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const businessID = computed(() => store.state.user.businessID);
    const isLoading = ref(false);
    const isSubmitting = ref(false);

    const blankSalesTypeData = {
      id: 0,
      name: '',
      code: '',
      prefix: '',
      isActive: true,
    };

    const salesTypeData = ref(JSON.parse(JSON.stringify(blankSalesTypeData)));

    const resetSalesTypeData = () => {
      salesTypeData.value = JSON.parse(
        JSON.stringify({
          ...blankSalesTypeData,
          businessID: businessID.value,
        })
      );
    };

    const fetchSalesTypeData = async () => {
      if (!props.selectedSalesTypeId) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-sales-type/fetchSalesType', {
          id: props.selectedSalesTypeId,
        });

        console.log('=== SalesTypeListEdit fetchSalesTypeData response:', response);

        // Handle different possible response structures
        let fetchedSalesType = null;

        if (response && response.salesType) {
          // If response has salesType wrapper
          fetchedSalesType = response.salesType;
        } else if (response && response.id) {
          // If response is the direct salesType object
          fetchedSalesType = response;
        } else if (response && response.data && response.data.salesType) {
          // If response has nested data structure
          fetchedSalesType = response.data.salesType;
        } else if (response && response.data && response.data.id) {
          // If response.data is the direct salesType object
          fetchedSalesType = response.data;
        } else {
          console.error('Unexpected response structure:', response);
          throw new Error('Invalid response structure from API');
        }

        // Populate form with fetched data
        salesTypeData.value = {
          id: fetchedSalesType.id || 0,
          name: fetchedSalesType.name || '',
          code: fetchedSalesType.code || '',
          prefix: fetchedSalesType.prefix || '',
          isActive: fetchedSalesType.isActive !== undefined ? fetchedSalesType.isActive : true,
        };

        console.log('=== SalesTypeListEdit populated form data:', salesTypeData.value);
      } catch (error) {
        console.error('Error fetching sales type:', error);
        // Reset to blank data on error
        resetSalesTypeData();
      } finally {
        isLoading.value = false;
      }
    };

    const onSubmit = async () => {
      isSubmitting.value = true;

      // Prepare data for submission - include the id
      const submissionData = {
        id: props.selectedSalesTypeId,
        name: salesTypeData.value.name,
        code: salesTypeData.value.code,
        prefix: salesTypeData.value.prefix,
        isActive: salesTypeData.value.isActive,
      };

      try {
        await store.dispatch('fbr-invoice-sales-type/editSalesType', submissionData);

        // Show success toast
        store.dispatch('app/showToast', {
          title: 'Success',
          text: 'Sales Type updated successfully',
          variant: 'success',
        });

        emit('refetch-data');
        emit('updated'); // This will hide the form as parent sets selectedSalesTypeId to null
      } catch (error) {
        console.error('Error updating sales type:', error);

        // Show user-friendly error message
        let errorMessage = 'Error updating sales type. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        store.dispatch('app/showToast', {
          title: 'Error',
          text: errorMessage,
          variant: 'danger',
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    // Watch for selectedSalesTypeId changes to fetch data
    watch(
      () => props.selectedSalesTypeId,
      (newId) => {
        if (newId) {
          fetchSalesTypeData();
        } else {
          resetSalesTypeData();
        }
      },
      { immediate: true }
    );

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetSalesTypeData);

    return {
      salesTypeData,
      isLoading,
      isSubmitting,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
#edit-sales-type-sidebar {
  .content-sidebar-header {
    padding: 1rem 1.5rem !important;
  }
}
</style>
